import { Button, Icon } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
    ScrollArea,
} from '@trustoo/components/modal/BaseModal'
import { getPresenceSubscriptionHistory } from 'actions/providers'
import clsx from 'clsx'
import { useAppDispatch } from 'hooks/hooks'
import {
    DiscountSource,
    DiscountStatus,
    DiscountType,
    PresenceSubscriptionHistoryItem,
    SOURCE_GENERAL,
} from 'interfaces/PresenceSubscription'
import React, { useEffect, useState } from 'react'
import PresenceDiscountList from './PresenceDiscountList'

interface PresenceSubscriptionHistoryModalProps {
    providerId: number
}

const PresenceSubscriptionHistoryModal = NiceModal.create(
    ({ providerId }: PresenceSubscriptionHistoryModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const [history, setHistory] = useState<PresenceSubscriptionHistoryItem[]>([])
        const [loading, setLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)
        const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

        useEffect(() => {
            if (providerId) {
                fetchHistory()
            }
        }, [providerId])

        const fetchHistory = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await dispatch(getPresenceSubscriptionHistory(providerId))
                setHistory(response?.history || [])
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch history')
            } finally {
                setLoading(false)
            }
        }

        const toggleRowExpansion = (subscriptionId: number) => {
            const newExpandedRows = new Set(expandedRows)
            if (newExpandedRows.has(subscriptionId)) {
                newExpandedRows.delete(subscriptionId)
            } else {
                newExpandedRows.add(subscriptionId)
            }
            setExpandedRows(newExpandedRows)
        }

        const formatDate = (dateString: string | null) => {
            if (!dateString) return 'N/A'
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
        }

        // Helper function to convert database dates to effective billing months for display
        const getEffectiveBillingMonth = (date: string | null) => {
            if (!date) return null
            const dateObj = new Date(date)
            return dateObj.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        }

        // Helper function to map discount data to the expected interface
        const mapDiscounts = (discounts: PresenceSubscriptionHistoryItem['discounts']) => {
            return discounts.map((discount) => ({
                ...discount,
                type: discount.type as DiscountType,
                status: discount.status as DiscountStatus,
                source: (discount.source as DiscountSource) || SOURCE_GENERAL,
                months_count: discount.months_count || undefined,
                expiry_date: discount.expiry_date || undefined,
            }))
        }

        const renderStatus = (status: string) => {
            const statusConfig = {
                active: { color: 'text-success', label: 'Active' },
                pending: { color: 'text-tertiary-500', label: 'Pending' },
                inactive: { color: 'text-error', label: 'Inactive' },
            }

            const config = statusConfig[status as keyof typeof statusConfig] || {
                color: 'text-grey-600',
                label: status,
            }

            return <span className={clsx('text-md font-bold', config.color)}>{config.label}</span>
        }

        const renderAmount = (amount: number | null) => {
            if (!amount) return 'N/A'
            const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
            return isNaN(numAmount) ? 'N/A' : '€' + numAmount.toFixed(2)
        }

        const renderDiscounts = (item: PresenceSubscriptionHistoryItem) => {
            const discountCount = item.discounts?.length || 0

            if (discountCount > 0) {
                return (
                    <Button
                        name="text"
                        onClick={() => toggleRowExpansion(item.id)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <span>Discounts({discountCount})</span>
                        <Icon
                            name={expandedRows.has(item.id) ? 'expand_less' : 'expand_more'}
                            className="w-4 h-4"
                        />
                    </Button>
                )
            } else {
                return <span className="text-grey-500">No Discounts</span>
            }
        }

        const onClose = () => {
            modal.resolve()
            modal.hide()
        }

        return (
            <BaseModal
                onClose={onClose}
                maxWidth="80vw"
                className="h-[80vh] w-[80vw]"
                scrollArea={ScrollArea.CONTENT}>
                <BaseModalHeader>
                    <h2 className="text-display-xs font-bold">Presence Subscription History</h2>
                </BaseModalHeader>
                <BaseModalMainContent>
                    {loading && (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                            <div className="flex">
                                <Icon name="warning" className="text-red-400 mr-2" />
                                <div className="text-red-800">{error}</div>
                            </div>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {history.length === 0 ? (
                                <div className="text-center py-8 text-neutral-500">
                                    No subscription history found
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y">
                                        <thead>
                                            <tr>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    ID
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    Service
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    Override Amount
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    Start Date
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    End Date
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">
                                                    Discounts
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y">
                                            {history.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <tr>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md font-medium">
                                                            {item.id}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {item.service_name}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {renderAmount(
                                                                item.subscription_override_amount,
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {renderStatus(item.status)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {formatDate(item.start_date)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {formatDate(item.end_date)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-md">
                                                            {renderDiscounts(item)}
                                                        </td>
                                                    </tr>
                                                    {expandedRows.has(item.id) && (
                                                        <tr>
                                                            <td
                                                                colSpan={7}
                                                                className="px-4 py-2 bg-grey-500">
                                                                <div className="ml-4">
                                                                    <PresenceDiscountList
                                                                        discounts={mapDiscounts(
                                                                            item.discounts || [],
                                                                        )}
                                                                        onDeleteDiscount={() => {}} // No delete functionality in history view
                                                                        getEffectiveBillingMonth={
                                                                            getEffectiveBillingMonth
                                                                        }
                                                                        hideDelete
                                                                        hideOptionalText
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </BaseModalMainContent>
                <BaseModalFooter>
                    <Button name="secondary" onClick={onClose}>
                        Close
                    </Button>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default PresenceSubscriptionHistoryModal
